import React, { useState, useEffect, useCallback } from "react";
import Timer from "./components/Timer";
import Sessions from "./components/Sessions";
import BlockedSites from "./components/BlockedSites";
import Stats from "./components/Stats";
import StatusBar from "./components/StatusBar";

const STORAGE_KEY = "deepWorkFocusTimer";

function App() {
  const [appData, setAppData] = useState({
    sessions: [],
    blockedSites: [
      "twitter.com",
      "facebook.com",
      "instagram.com",
      "reddit.com",
      "youtube.com",
      "tiktok.com",
    ],
    stats: {
      totalMinutes: 0,
      todayMinutes: 0,
      weekMinutes: 0,
      totalSessions: 0,
      lastSessionDate: null,
      streak: 0,
    },
    activeSession: null,
    isPaused: false,
    timeRemaining: 0,
  });

  const [extensionConnected, setExtensionConnected] = useState(false);
  const [extensionStatus, setExtensionStatus] = useState("checking");

  // Auto-sync with extension
  const syncWithExtension = useCallback(() => {
    if (extensionConnected) {
      window.postMessage(
        {
          type: "FOCUS_TIMER_UPDATE",
          activeSession: appData.activeSession,
          blockedSites: appData.blockedSites,
          timeRemaining: appData.timeRemaining,
          isActive: !!appData.activeSession,
        },
        "*"
      );
    }
  }, [appData, extensionConnected]);

  // Save data and sync with extension
  const saveData = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(appData));
    syncWithExtension();
  }, [appData, syncWithExtension]);

  // Load data from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setAppData((prev) => ({ ...prev, ...parsed }));
      } catch (error) {
        console.error("Error loading stored data:", error);
      }
    }
  }, []);

  // Extension communication setup
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.source !== window) return;

      switch (event.data.type) {
        case "EXTENSION_CONNECTED":
          console.log("Extension connected to React app");
          setExtensionConnected(true);
          setExtensionStatus("connected");
          // Notify extension that React app is ready
          window.postMessage({ type: "REACT_APP_READY" }, "*");
          break;

        case "EXTENSION_READY":
          console.log("Extension ready with data:", event.data.data);
          setExtensionConnected(true);
          setExtensionStatus("ready");
          // Restore session from extension if exists
          if (event.data.data?.activeSession) {
            setAppData((prev) => ({
              ...prev,
              activeSession: event.data.data.activeSession,
              timeRemaining: event.data.data.timeRemaining || 0,
            }));
          }
          break;

        case "EXTENSION_DATA_RESPONSE":
          console.log("Received extension data:", event.data.data);
          if (event.data.data?.activeSession) {
            setAppData((prev) => ({
              ...prev,
              activeSession: event.data.data.activeSession,
              timeRemaining: event.data.data.timeRemaining || 0,
            }));
          }
          break;

        case "BLOCKING_STATUS_CHANGED":
          console.log("Blocking status changed:", event.data);
          break;

        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);

    // Auto-request extension connection
    const requestExtensionConnection = () => {
      window.postMessage({ type: "REQUEST_EXTENSION_DATA" }, "*");
      window.postMessage({ type: "REACT_APP_READY" }, "*");
    };

    // Try connecting immediately and then every 2 seconds until connected
    requestExtensionConnection();
    const connectionInterval = setInterval(() => {
      if (!extensionConnected) {
        requestExtensionConnection();
      } else {
        clearInterval(connectionInterval);
      }
    }, 2000);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearInterval(connectionInterval);
    };
  }, [extensionConnected]);

  // Auto-save when data changes
  useEffect(() => {
    saveData();
  }, [saveData]);

  // Auto-sync with extension when relevant data changes
  useEffect(() => {
    syncWithExtension();
  }, [
    appData.activeSession,
    appData.blockedSites,
    appData.timeRemaining,
    syncWithExtension,
  ]);

  const updateAppData = (updates) => {
    setAppData((prev) => ({ ...prev, ...updates }));
  };

  const openExtensionRepo = () => {
    window.open("https://github.com/supersver/Deep-Focus-Extension", "_blank");
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100"
      data-testid="focus-timer-app"
    >
      <div className="max-w-[1400px] mx-auto py-8 px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <i className="ph ph-brain text-5xl text-blue-600"></i>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Deep Work Focus Timer
            </h1>
          </div>
          <p className="text-gray-600 text-lg font-medium">
            Professional Focus Management & Website Blocking
          </p>
        </div>

        {/* Extension Status */}
        {extensionStatus === "checking" && (
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <div className="animate-spin h-5 w-5 border-2 border-yellow-600 border-t-transparent rounded-full mr-3"></div>
              <span className="text-yellow-800 font-medium">
                Connecting to browser extension...
              </span>
            </div>
          </div>
        )}

        {extensionStatus === "connected" && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <i className="ph ph-check-circle text-green-500 text-xl mr-3"></i>
              <span className="text-green-800 font-semibold">
                Extension connected! Website blocking is active.
              </span>
            </div>
          </div>
        )}

        {!extensionConnected && extensionStatus !== "checking" && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <i className="ph ph-warning-circle text-red-500 text-xl mr-3"></i>
                <span className="text-red-800 font-semibold">
                  Extension not detected. Install the browser extension for
                  website blocking.
                </span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={openExtensionRepo}
                  className="text-sm bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2"
                >
                  <i className="ph ph-github-logo"></i>
                  Get Extension
                </button>
                <button
                  onClick={() => window.open("chrome://extensions/", "_blank")}
                  className="text-sm bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-200 flex items-center gap-2"
                >
                  <i className="ph ph-gear"></i>
                  Manage
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Status Bar */}
        <StatusBar appData={appData} extensionConnected={extensionConnected} />

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Timer - Full Width */}
          <div className="lg:col-span-3">
            <Timer appData={appData} updateAppData={updateAppData} />
          </div>

          {/* Sessions */}
          <div className="lg:col-span-1">
            <Sessions appData={appData} updateAppData={updateAppData} />
          </div>

          {/* Blocked Sites */}
          <div className="lg:col-span-1">
            <BlockedSites appData={appData} updateAppData={updateAppData} extensionStatus={extensionStatus} />
          </div>

          {/* Stats */}
          <div className="lg:col-span-1">
            <Stats appData={appData} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
